import * as React from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";

/**
 * Calendar — Outdated/Legacy Styling with Darker Grey Theme and Tennissh Greenish Accents
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  locale,
  formatters,
  components,
  ...props
}) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      className={cn("bg-[#1c1c1c] text-gray-300 p-2 border border-[#2d2d2d] font-sans", className)}
      classNames={{
        root:    "w-fit",
        months:  "flex flex-col sm:flex-row gap-2",
        month:   "relative flex flex-col gap-1 border border-[#2c2c2c] p-1 bg-[#141414]",
        month_caption: "flex h-7 items-center justify-center bg-[#222222] border-b border-[#2c2c2c] relative text-gray-300",
        caption_label: "text-xs font-bold font-sans",
        
        button_previous: "absolute left-1 top-1 h-5 w-5 p-0 bg-[#2a2a2a] text-gray-300 border border-[#3c3c3c] rounded-none hover:bg-[#3a3a3a] font-bold flex items-center justify-center text-[10px] z-10",
        button_next:     "absolute right-1 top-1 h-5 w-5 p-0 bg-[#2a2a2a] text-gray-300 border border-[#3c3c3c] rounded-none hover:bg-[#3a3a3a] font-bold flex items-center justify-center text-[10px] z-10",

        month_grid: "w-full border-collapse border border-[#2c2c2c] bg-[#121212]",
        weekdays:   "flex bg-[#222222] border-b border-[#2c2c2c]",
        weekday:    "w-8 h-7 flex items-center justify-center text-[10px] font-bold text-gray-400 border-r border-[#2c2c2c] last:border-r-0 select-none",
        week:       "flex border-b border-[#2c2c2c] last:border-b-0",
        week_number_header: "w-8 select-none border-r border-[#2c2c2c]",
        week_number: "text-[0.7rem] text-gray-500 select-none text-center border-r border-[#2c2c2c] last:border-r-0",

        day: "group/day relative h-8 w-8 p-0 text-center select-none border-r border-[#2c2c2c] last:border-r-0",
        range_start:  "bg-[#a3e635] text-black rounded-none",
        range_middle: "bg-[#3f5217]/55 text-gray-200 rounded-none",
        range_end:    "bg-[#a3e635] text-black rounded-none",
        today:        "bg-gray-800 text-[#a3e635] font-bold",
        outside:      "bg-[#0f0f0f] text-gray-655",
        disabled:     "bg-[#111111] text-gray-700 pointer-events-none line-through",
        hidden:       "invisible",

        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === "left")  return <span className="font-bold text-[9px]">&lt;</span>;
          if (orientation === "right") return <span className="font-bold text-[9px]">&gt;</span>;
          return <span className="font-bold text-[9px]">v</span>;
        },
        DayButton: (dayBtnProps) => (
          <CalendarDayButton locale={locale} {...dayBtnProps} />
        ),
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({ className, day, modifiers, locale, ...props }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isSelected = modifiers.selected || modifiers.range_start || modifiers.range_end;
  const isSingle =
    modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={isSingle || undefined}
      data-range-start={modifiers.range_start || undefined}
      data-range-end={modifiers.range_end || undefined}
      data-range-middle={modifiers.range_middle || undefined}
      data-today={modifiers.today || undefined}
      data-outside={modifiers.outside || undefined}
      data-disabled={modifiers.disabled || undefined}
      className={cn(
        "relative z-10 h-8 w-8 p-0 text-[11px] font-sans text-gray-300 rounded-none bg-transparent border-0 flex items-center justify-center outline-none",
        "hover:bg-[#222222] hover:text-white",
        
        // Today styling is ONLY applied if it is today and NOT selected/in-range
        modifiers.today && !isSelected && !modifiers.range_middle && "bg-gray-800 text-[#a3e635] font-bold border border-[#a3e635]/50",
        
        // Single selection
        isSingle && "bg-[#a3e635] text-black font-bold border border-[#a3e635]",
        
        // Range start/end
        (modifiers.range_start || modifiers.range_end) && "bg-[#a3e635] text-black font-bold border border-[#a3e635]",
        
        // Range middle
        modifiers.range_middle && "bg-[#3f5217]/55 text-gray-200",
        
        // Subtle outline for today when selected/in-range
        modifiers.today && isSelected && "border border-black font-bold",
        modifiers.today && modifiers.range_middle && "border border-[#a3e635]/50 font-bold",
        
        modifiers.outside && "text-gray-655",
        modifiers.disabled && "text-gray-700 pointer-events-none line-through bg-[#111111]",
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
